import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import type { AddressPrediction } from "../../../types/address";
import {
  ADDRESS_SEARCH_MIN_LENGTH,
  fetchAddressPredictions,
  resolveAddressPlace,
  type AddressPredictionWithPlace,
  type AddressSearchSession,
} from "../../lib/fetchAddressPredictions";
import { loadGoogleMapsPlaces } from "../../lib/googleMapsPlacesLoader";
import { useDebouncedValue } from "../../lib/useDebouncedValue";
import { cn } from "../../lib/utils";
import { FieldLabel, TextInput } from "../ui/FormControls";

const SEARCH_DEBOUNCE_MS = 300;

export type AddressResolvedLocation = {
  formattedAddress: string;
  placeId: string;
  lat: number;
  lng: number;
};

type AddressAutocompleteFieldProps = {
  id: string;
  label: string;
  addressFieldName: string;
  placeIdFieldName: string;
  latFieldName: string;
  lngFieldName: string;
  defaultValue?: string;
  defaultLat?: number | null;
  defaultLng?: number | null;
  googleMapsApiKey?: string | null;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  onResolved?: (location: AddressResolvedLocation | null) => void;
};

export const AddressAutocompleteField: React.FC<
  AddressAutocompleteFieldProps
> = ({
  id,
  label,
  addressFieldName,
  placeIdFieldName,
  latFieldName,
  lngFieldName,
  defaultValue = "",
  defaultLat,
  defaultLng,
  googleMapsApiKey,
  placeholder = "Start typing a street address",
  helperText,
  required = true,
  onResolved,
}) => {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const predictionsRef = useRef<Map<string, AddressPredictionWithPlace>>(
    new Map(),
  );
  const sessionRef = useRef<AddressSearchSession>({ token: null });
  const requestIdRef = useRef(0);

  const [query, setQuery] = useState(defaultValue);
  const [confirmedAddress, setConfirmedAddress] = useState(() => {
    if (
      defaultValue &&
      defaultLat != null &&
      defaultLng != null &&
      Number.isFinite(defaultLat) &&
      Number.isFinite(defaultLng)
    ) {
      return defaultValue;
    }
    return "";
  });
  const [suggestions, setSuggestions] = useState<AddressPrediction[]>([]);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [placesReady, setPlacesReady] = useState(false);
  const [placesUnavailable, setPlacesUnavailable] = useState(false);
  const [pickRequired, setPickRequired] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const [selectedLat, setSelectedLat] = useState(
    defaultLat != null && Number.isFinite(defaultLat) ? String(defaultLat) : "",
  );
  const [selectedLng, setSelectedLng] = useState(
    defaultLng != null && Number.isFinite(defaultLng) ? String(defaultLng) : "",
  );

  const apiKey = googleMapsApiKey?.trim() || "";
  const autocompleteEnabled = Boolean(apiKey) && !placesUnavailable;
  const hasPinnedCoords = Boolean(selectedLat && selectedLng);
  const requirePick = autocompleteEnabled && placesReady && !hasPinnedCoords;
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const describedBy = [`${id}-helper`, pickRequired ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (!apiKey || typeof window === "undefined") {
      return;
    }

    let cancelled = false;
    loadGoogleMapsPlaces(apiKey).then(
      () => {
        if (!cancelled) {
          setPlacesReady(true);
        }
      },
      () => {
        if (!cancelled) {
          setPlacesUnavailable(true);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  useEffect(() => {
    if (!autocompleteEnabled || !placesReady) {
      return;
    }

    if (
      debouncedQuery.length < ADDRESS_SEARCH_MIN_LENGTH ||
      debouncedQuery === confirmedAddress
    ) {
      setSuggestions([]);
      setSearching(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setSearching(true);

    fetchAddressPredictions({
      googleMapsApiKey: apiKey,
      query: debouncedQuery,
      session: sessionRef.current,
    })
      .then((results) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        predictionsRef.current = new Map(
          results.map((prediction) => [prediction.placeId, prediction]),
        );
        setSuggestions(results);
        setHighlightIndex(0);
        setListOpen(results.length > 0);
        setSearching(false);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setPlacesUnavailable(true);
        setSuggestions([]);
        setSearching(false);
      });
  }, [
    apiKey,
    autocompleteEnabled,
    confirmedAddress,
    debouncedQuery,
    placesReady,
  ]);

  const clearSelection = useCallback(() => {
    setSelectedPlaceId("");
    setSelectedLat("");
    setSelectedLng("");
    setConfirmedAddress("");
    onResolved?.(null);
  }, [onResolved]);

  const selectPrediction = useCallback(
    async (prediction: AddressPrediction) => {
      const live = predictionsRef.current.get(prediction.placeId) ?? null;
      if (!live) {
        return;
      }

      const resolved = await resolveAddressPlace(live);
      if (!resolved) {
        setPickRequired(true);
        return;
      }

      sessionRef.current.token = null;
      setQuery(resolved.formattedAddress);
      setConfirmedAddress(resolved.formattedAddress);
      setSelectedPlaceId(resolved.placeId);
      setSelectedLat(String(resolved.lat));
      setSelectedLng(String(resolved.lng));
      setSuggestions([]);
      setListOpen(false);
      setPickRequired(false);
      onResolved?.({
        formattedAddress: resolved.formattedAddress,
        placeId: resolved.placeId,
        lat: resolved.lat,
        lng: resolved.lng,
      });
    },
    [onResolved],
  );

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setListOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form || !requirePick) {
      return;
    }

    const onSubmit = (event: Event) => {
      if (!selectedPlaceId) {
        event.preventDefault();
        setPickRequired(true);
        setListOpen(suggestions.length > 0);
      }
    };

    form.addEventListener("submit", onSubmit);
    return () => form.removeEventListener("submit", onSubmit);
  }, [requirePick, selectedPlaceId, suggestions.length]);

  const defaultHelper = autocompleteEnabled
    ? "Start typing and pick an address from the list."
    : "Include the city so we can pin it on the map.";

  return (
    <div ref={containerRef}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="relative">
        <div className="relative">
          <TextInput
            ref={inputRef}
            id={id}
            name={addressFieldName}
            value={query}
            placeholder={placeholder}
            required={required}
            autoComplete="off"
            className={searching ? "pr-10" : undefined}
            role={autocompleteEnabled ? "combobox" : undefined}
            aria-autocomplete={autocompleteEnabled ? "list" : undefined}
            aria-expanded={autocompleteEnabled ? listOpen : undefined}
            aria-controls={autocompleteEnabled ? listboxId : undefined}
            aria-activedescendant={
              autocompleteEnabled && listOpen && suggestions[highlightIndex]
                ? `${listboxId}-option-${highlightIndex}`
                : undefined
            }
            aria-describedby={describedBy}
            onChange={(event) => {
              setQuery(event.target.value);
              clearSelection();
              setPickRequired(false);
              if (
                event.target.value.trim().length >= ADDRESS_SEARCH_MIN_LENGTH
              ) {
                setListOpen(true);
              }
            }}
            onFocus={() => {
              if (suggestions.length > 0) {
                setListOpen(true);
              }
            }}
            onKeyDown={(event) => {
              if (
                !autocompleteEnabled ||
                !listOpen ||
                suggestions.length === 0
              ) {
                return;
              }

              if (event.key === "ArrowDown") {
                event.preventDefault();
                setHighlightIndex((index) => (index + 1) % suggestions.length);
                return;
              }

              if (event.key === "ArrowUp") {
                event.preventDefault();
                setHighlightIndex(
                  (index) =>
                    (index - 1 + suggestions.length) % suggestions.length,
                );
                return;
              }

              if (event.key === "Enter") {
                const suggestion = suggestions[highlightIndex];
                if (suggestion) {
                  event.preventDefault();
                  void selectPrediction(suggestion);
                }
                return;
              }

              if (event.key === "Escape") {
                event.preventDefault();
                setListOpen(false);
              }
            }}
          />
          {searching ? (
            <Loader2
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
              aria-hidden
            />
          ) : null}
        </div>
        {autocompleteEnabled && listOpen && suggestions.length > 0 ? (
          <ul
            id={listboxId}
            role="listbox"
            className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-border bg-card p-1 shadow-md"
          >
            {suggestions.map((suggestion, index) => {
              const highlighted = index === highlightIndex;
              return (
                <li key={suggestion.placeId} role="presentation">
                  <button
                    type="button"
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={highlighted}
                    className={cn(
                      "flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm",
                      highlighted ? "bg-muted" : "hover:bg-muted/70",
                    )}
                    onMouseEnter={() => setHighlightIndex(index)}
                    onClick={() => {
                      void selectPrediction(suggestion);
                    }}
                  >
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span>
                      <span className="block font-medium text-foreground">
                        {suggestion.mainText}
                      </span>
                      {suggestion.secondaryText ? (
                        <span className="block text-muted-foreground">
                          {suggestion.secondaryText}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <input type="hidden" name={placeIdFieldName} value={selectedPlaceId} />
      <input type="hidden" name={latFieldName} value={selectedLat} />
      <input type="hidden" name={lngFieldName} value={selectedLng} />
      <p id={`${id}-helper`} className="mt-2 text-sm text-muted-foreground">
        {helperText ?? defaultHelper}
      </p>
      {pickRequired ? (
        <p
          id={`${id}-error`}
          className="mt-1 text-sm text-destructive"
          role="alert"
        >
          Pick an address from the list so we can pin the job site.
        </p>
      ) : null}
    </div>
  );
};
